import { useState, useMemo, useCallback, useEffect } from "react";
import Datagrid from "components/Datagrid/Datagrid";
import type { SelectValue } from "ui-kit/src/MultipleSelect/types";
import { useVideoSourceData } from "../../../../hooks/apiManagement/useVideoSourceData";
import { useVideoSourceOptions } from "../../../../hooks/apiManagement/useVideoSourceOptions";
import { VideoSourceTableCell } from "../../../../components/VideoTasksPage/VideoSourceTableCell";
import { VideoSourceHeaderFilters } from "../../../../components/VideoTasksPage/VideoSourceHeaderFilters";
import { DieLigenApiManagementConstants } from "./DieLigenApiManagementConstants";
import { _, _any } from "i18n";
import { StatusCell } from "../../../../components/VideoTasksPage/StatusCell";
import type { CellProps } from "react-table";
import { TeamsheetModal } from "../../../../components/VideoTasksPage/TeamsheetModal";
import { useClubList } from "../../../../hooks/apiManagement/useClubList";
import { Task } from "../../../../types/apiManagement";
import { getClubId } from "../../../../helpers/apiManagement/teamsheetHelpers";
import "../../../../style/dieLigen.css";
import { TableStatus } from "../../../../components/VideoTasksPage/TableStatus";
import { SortingColumn, SortingState, updateSortingState } from "../../../../helpers/apiManagement/sorting";

interface HeaderFilterState {
	league: SelectValue | null;
	videoStatus: SelectValue | null;
	teamsheetStatus: SelectValue | null;
	homeTeam: SelectValue | null;
	awayTeam: SelectValue | null;
	country: SelectValue | null;
	category: SelectValue | null;
	eyeball_game_id: SelectValue | null;
}

export type ModalState = {
	isOpen: boolean;
	selectedTaskId?: string;
	homeTeam: string;
	awayTeam: string;
	homeTeamId: number;
	awayTeamId: number;
}

export function DieLigenTable() {
	const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
	const [sorting, setSorting] = useState<SortingState>({ fields: {} });
	const [hasMoreData, setHasMoreData] = useState(true);
	const [accumulatedVideos, setAccumulatedVideos] = useState<Task[]>([]);
	const [headerFilters, setHeaderFilters] = useState<HeaderFilterState>({
		league: null,
		videoStatus: null,
		teamsheetStatus: null,
		homeTeam: null,
		awayTeam: null,
		country: null,
		category: null,
		eyeball_game_id: null
	});
	const { findSimilarClubs } = useClubList();
	const [modalState, setModalState] = useState<ModalState>({ isOpen: false, homeTeam: "", awayTeam: "", homeTeamId: 0, awayTeamId: 0 });
	const sortableColumns: string[] = ['gameDate', 'updatedAt']

	const { tasksData, isLoading, isFetching } = useVideoSourceData(pagination, {
		league: headerFilters.league,
		video_status: headerFilters.videoStatus,
		teamsheet_status: headerFilters.teamsheetStatus,
		home_team: headerFilters.homeTeam,
		away_team: headerFilters.awayTeam,
		country: headerFilters.country,
		category: headerFilters.category,
		eyeball_game_id: headerFilters.eyeball_game_id
	}, sorting);

	useEffect(() => {
		if (!tasksData?.videos) return;

		if (pagination.pageIndex === 0) {
			setAccumulatedVideos(tasksData.videos);
		} else {
			const existingVideoIds = new Set(accumulatedVideos.map(video => video.videoId));
			const newVideos = tasksData.videos.filter(video => !existingVideoIds.has(video.videoId));
			
			setAccumulatedVideos(prev => [...prev, ...newVideos]);
		}
		
		setHasMoreData(tasksData.videos.length === pagination.pageSize);
	}, [tasksData, pagination.pageIndex]);

	const { 
		leagueOptions, 
		teamOptions, 
		isTeamOptionsLoading, 
		isLeagueOptionsLoading,
		countryOptions,
		isCountryOptionsLoading
	} = useVideoSourceOptions();

	const handleHeaderFilterChange = useCallback((field: keyof HeaderFilterState, value: SelectValue) => {
		setHeaderFilters(prev => ({
			...prev,
			[field]: value
		}));
		setPagination(prev => ({ ...prev, pageIndex: 0 }));
		setHasMoreData(!!value);
	}, []);

	const handlePaginationChange = useCallback((pageSize: number, pageIndex: number) => {
		if (pageSize !== pagination.pageSize) {
			setPagination({ pageIndex: 0, pageSize });
			setHasMoreData(true);
			setAccumulatedVideos([]);
		} else if (pageIndex !== pagination.pageIndex) {
			setPagination(prev => ({ ...prev, pageIndex }));
		}
	}, [pagination.pageSize, pagination.pageIndex]);

	const handleLoadMore = useCallback(() => {
		setPagination(prev => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
	}, []);

	const handleTeamsheetClick = (task: Task) => {
		setModalState({
			isOpen: true, 
			selectedTaskId: task.taskId,
			homeTeam: task.homeTeam || "",
			awayTeam: task.awayTeam || "",
			homeTeamId: task.homeTeamId || 0,
			awayTeamId: task.awayTeamId || 0,
		});
	};

	const handleSort = useCallback((column: { id: string, desc: boolean } | null) => {
		setSorting(prev => updateSortingState(prev, column as SortingColumn, sortableColumns));
		setPagination(prev => ({ ...prev, pageIndex: 0 }));
		setHasMoreData(true);
	}, []);

	const handleFilterClick = useCallback(() => {
		setHeaderFilters(prev => ({
			...prev,
			teamsheetStatus: {
				value: DieLigenApiManagementConstants.ALL_UNMAPPED.join(","),
				label: _("video-tasks.table.filter.all_unmapped_clubs")
			}
		}));
		setPagination(prev => ({ ...prev, pageIndex: 0 }));
		setHasMoreData(true);
	}, []);

	const transformedVideos = useMemo<Task[]>(() => {
		return accumulatedVideos.map(video => ({
			...video,
			videoUrl: video.videoUrl || video.videoId || '',
			homeTeamId: Number(video.homeTeamId) || 0,
			awayTeamId: Number(video.awayTeamId) || 0,
			taskId: video.taskId || video.videoId || ''
		}));
	}, [accumulatedVideos]);

	const headerRenderers = useMemo(() => 
		Object.fromEntries(
			Object.keys(DieLigenApiManagementConstants.columns).map(key => [
				key,
				() => {
					const renderer = VideoSourceHeaderFilters[key as keyof typeof VideoSourceHeaderFilters];
					if (!renderer) {
						return null;
					}
					
					if (['league', 'homeTeam', 'awayTeam', 'videoStatus', 'teamsheetStatus', 'leagueCountry', 'eyeballGameId'].includes(key)) {
						return renderer({
							headerFilters,
							onFilterChange: handleHeaderFilterChange,
							leagueOptions,
							teamOptions,
							countryOptions,
							isTeamOptionsLoading,
							isLeagueOptionsLoading,
							isCountryOptionsLoading
						});
					}
					
					return (renderer as () => JSX.Element)();
				}
			])
		),
		[headerFilters, handleHeaderFilterChange, leagueOptions, teamOptions, isTeamOptionsLoading, isLeagueOptionsLoading, countryOptions, isCountryOptionsLoading]
	);

	const shouldShowLoadMore = useMemo(() => {
		return hasMoreData && !isLoading;
	}, [hasMoreData, isLoading]);

	const cellRenderers = useMemo(() => ({
		...VideoSourceTableCell,
		teamsheetStatus: (props: CellProps<Task>) => (
			<StatusCell 
				{...props} 
				type="teamsheet" 
				onTeamsheetClick={() => {
					const task = props.cell.row.original;
					if (task) handleTeamsheetClick(task);
				}}
			/>
		)
	}), [handleTeamsheetClick]);

	const showEmptyState = !isLoading && accumulatedVideos.length === 0;
	const showEndOfList = !isLoading && !hasMoreData && accumulatedVideos.length > 0;

	const fetchClubId = (type: "home" | "away") => {
		return Number(getClubId(modalState[`${type}Team`], findSimilarClubs))
	}

	return (
		<div className="datatable-container">
			<Datagrid
				data={transformedVideos}
				columns={DieLigenApiManagementConstants.columns}
				headerRenderers={headerRenderers}
				cellRenderers={cellRenderers}
				override={true}
				showLoader={isLoading}
				lengthFilterEnabled={true}
				className="table"
				sortableColumns={sortableColumns}
				manualPaginationEnabled={false}
				manualSortBy={true}
				onPaginate={handlePaginationChange}
				onSortBy={handleSort}
				defaultLength={pagination.pageSize}
				columnSortTypes={{
					gameDate: 'datetime',
					updatedAt: 'datetime'
				}}
				buttonFilterEnabled={true}
				buttonFilterLabel={_("video-tasks.table.filter.label")}
				onButtonFilterClick={handleFilterClick}
			/>
			<TableStatus
				isLoading={isLoading}
				showEmptyState={showEmptyState}
				showEndOfList={showEndOfList}
				shouldShowLoadMore={shouldShowLoadMore}
				onLoadMore={handleLoadMore}
				isFetching={isFetching}
			/>
			<TeamsheetModal
				isOpen={modalState.isOpen}
				onClose={() => setModalState({ isOpen: false, homeTeam: "", awayTeam: "", homeTeamId: 0, awayTeamId: 0 })}
				homeTeam={modalState.homeTeam}
				awayTeam={modalState.awayTeam}
				taskId={modalState.selectedTaskId as string}
				homeTeamId={modalState.homeTeamId || Number(fetchClubId("home"))}
				awayTeamId={modalState.awayTeamId || Number(fetchClubId("away"))}
			/>
		</div>
	);
}
